import {createSlice, PayloadAction} from '@reduxjs/toolkit';

/**
 * Global error-toast slice.
 *
 * Backend/CustomException errors surfaced by the axios interceptor (see
 * `src/helpers/backendErrorHandler.ts`) are pushed here, then rendered by the
 * single global `GlobalErrorSnackbar` host mounted in `src/app/index.tsx`.
 *
 * Keeping the queue in Redux (instead of inside each page HOC) is what allows
 * the interceptor — code that lives outside any React component — to notify the
 * user about every backend exception with a localized message.
 */

export type ErrorToastSeverity = 'error' | 'warning' | 'info' | 'success';

export interface ErrorToastPayload {
    /** i18n message id resolved for this error, e.g. `boilerplate.components.Errors.EMPRUNT_ID_MUST_BE_NOT_NULL`. */
    messageId?: string;
    /** Plain-text fallback when no i18n key applies (used only by manual callers). */
    message?: string;
    severity?: ErrorToastSeverity;
    duration?: number;
}

export interface ErrorToastItem extends ErrorToastPayload {
    /** Unique id so the host can restart its auto-hide timer for each new toast. */
    id: number;
}

export interface ErrorToastState {
    queue: ErrorToastItem[];
}

/** Keep the queue small: a burst of failed requests must not flood the screen. */
const MAX_QUEUE_SIZE = 4;
const DEFAULT_DURATION = 6000;

const initialState: ErrorToastState = {
    queue: [],
};

let toastSequence = 0;

const errorToastSlice = createSlice({
    name: 'errorToast',
    initialState,
    reducers: {
        pushErrorToast: {
            reducer(state, action: PayloadAction<ErrorToastItem>) {
                if (state.queue.length >= MAX_QUEUE_SIZE) {
                    state.queue.shift();
                }
                state.queue.push(action.payload);
            },
            prepare(payload: ErrorToastPayload): { payload: ErrorToastItem } {
                toastSequence += 1;
                return {
                    payload: {
                        id: toastSequence,
                        severity: payload.severity ?? 'error',
                        duration: payload.duration ?? DEFAULT_DURATION,
                        ...(payload.messageId ? {messageId: payload.messageId} : {}),
                        ...(payload.message ? {message: payload.message} : {}),
                    },
                };
            },
        },
        /** Drop the currently displayed toast; the host calls this after it closes. */
        shiftErrorToast(state) {
            state.queue.shift();
        },
        clearErrorToasts(state) {
            state.queue = [];
        },
    },
});

export const {pushErrorToast, shiftErrorToast, clearErrorToasts} = errorToastSlice.actions;

export default errorToastSlice.reducer;
